import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, CheckCircle, AlertCircle } from 'lucide-react';

const ResetPassword = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t('resetPassword.errors.invalidToken'));
    }
  }, [token, t]);

  const getApiUrl = () => {
    if (window.location.hostname === 'localhost') {
      return 'http://localhost:3001/api';
    }
    return 'https://app.alpenscreen.ch/api';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (newPassword.length < 8) {
      setError(t('resetPassword.errors.tooShort'));
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('resetPassword.errors.noMatch'));
      setLoading(false);
      return;
    }

    try {
      const API_URL = getApiUrl();
      const response = await fetch(`${API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('resetPassword.errors.failed'));
      }

      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (err) {
      console.error('Reset password error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 16px 14px 44px',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    fontSize: '15px',
    transition: 'all 0.2s ease',
    outline: 'none',
    fontFamily: 'inherit'
  };

  const iconContainerStyle = {
    position: 'absolute',
    left: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#94a3b8'
  };

  if (success) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #2B7FD7 0%, #5CA3EC 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '20px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
          maxWidth: '480px',
          width: '100%',
          padding: '60px 40px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <CheckCircle size={40} color="white" />
          </div>
          
          <h1 style={{
            fontSize: '28px',
            color: '#1e293b',
            marginBottom: '12px',
            fontWeight: '700'
          }}>
            {t('resetPassword.success.title')}
          </h1>
          
          <p style={{
            fontSize: '15px',
            color: '#64748b',
            marginBottom: '24px',
            lineHeight: '1.6'
          }}>
            {t('resetPassword.success.message')}
          </p>

          <button
            onClick={() => navigate('/login')}
            style={{
              background: 'linear-gradient(135deg, #2B7FD7 0%, #5CA3EC 100%)',
              color: 'white',
              padding: '14px 32px',
              border: 'none',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 12px rgba(43, 127, 215, 0.3)'
            }}
          >
            {t('resetPassword.success.loginButton')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #2B7FD7 0%, #5CA3EC 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '20px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
        maxWidth: '480px',
        width: '100%',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* BETA Badge */}
        <div style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          background: 'linear-gradient(135deg, #bae6fd 0%, #7dd3fc 100%)',
          color: '#0c4a6e',
          padding: '6px 14px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: '700',
          letterSpacing: '1px',
          border: '2px solid #0ea5e9',
          boxShadow: '0 2px 8px rgba(14, 165, 233, 0.2)'
        }}>
          {t('resetPassword.beta')}
        </div>

        <div style={{ padding: '50px 40px 30px' }}>
          {/* Logo - Clickable */}
          <div 
            onClick={() => navigate('/')}
            style={{
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '24px'
            }}
          >
            <img 
              src="/assets/logo/alpenscreen-horizontal-transparent.svg" 
              alt="AlpenScreen" 
              style={{
                height: '55px',
                width: 'auto'
              }}
              onError={(e) => {
                e.target.src = '/assets/logo/alpenscreen-horizontal-512px.png';
              }}
            />
          </div>

          {/* Swiss Badge */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
              padding: '8px 16px',
              borderRadius: '50px',
              fontSize: '13px',
              fontWeight: '600',
              border: '1px solid #93c5fd',
              color: '#1e40af'
            }}>
              <svg 
                viewBox="0 0 32 32" 
                width="18" 
                height="18"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="32" height="32" fill="#DA291C"/>
                <rect x="13" y="6" width="6" height="20" fill="white"/>
                <rect x="6" y="13" width="20" height="6" fill="white"/>
              </svg>
              <span>{t('resetPassword.swissMade')}</span>
            </div>
          </div>

          <h1 style={{
            fontSize: '28px',
            color: '#1e293b',
            marginBottom: '12px',
            textAlign: 'center',
            fontWeight: '700'
          }}>
            {t('resetPassword.title')}
          </h1>
          
          <p style={{
            fontSize: '15px',
            color: '#64748b',
            textAlign: 'center',
            marginBottom: '0',
            lineHeight: '1.5'
          }}>
            {t('resetPassword.subtitle')}
          </p>
        </div>

        {/* Blue Separator Bar */}
        <div style={{
          height: '4px',
          background: 'linear-gradient(90deg, #2B7FD7 0%, #5CA3EC 100%)',
          boxShadow: '0 2px 8px rgba(43, 127, 215, 0.2)'
        }}></div>

        <div style={{ padding: '40px 40px 50px' }}>

          {error && (
            <div style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#dc2626',
              padding: '14px',
              borderRadius: '12px',
              marginBottom: '24px',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <AlertCircle size={18} />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#334155',
                marginBottom: '10px'
              }}>
                {t('resetPassword.newPassword')}
              </label>
              <div style={{ position: 'relative' }}>
                <div style={iconContainerStyle}>
                  <Lock size={18} />
                </div>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  style={inputStyle}
                  placeholder={t('resetPassword.newPasswordPlaceholder')}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2B7FD7';
                    e.target.style.boxShadow = '0 0 0 4px rgba(43, 127, 215, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
              <p style={{
                fontSize: '13px',
                color: '#64748b',
                marginTop: '8px',
                marginBottom: '0'
              }}>
                {t('resetPassword.passwordHint')}
              </p>
            </div>

            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#334155',
                marginBottom: '10px'
              }}>
                {t('resetPassword.confirmPassword')}
              </label>
              <div style={{ position: 'relative' }}>
                <div style={iconContainerStyle}>
                  <Lock size={18} />
                </div>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  style={inputStyle}
                  placeholder={t('resetPassword.confirmPasswordPlaceholder')}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2B7FD7';
                    e.target.style.boxShadow = '0 0 0 4px rgba(43, 127, 215, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                background: loading ? '#94a3b8' : 'linear-gradient(135deg, #2B7FD7 0%, #5CA3EC 100%)',
                color: 'white',
                padding: '16px',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: loading ? 'none' : '0 4px 12px rgba(43, 127, 215, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(43, 127, 215, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(43, 127, 215, 0.3)';
                }
              }}
            >
              {loading ? t('resetPassword.submitting') : t('resetPassword.submitButton')}
            </button>
          </form>

          <p style={{
            textAlign: 'center',
            marginTop: '28px',
            fontSize: '14px',
            color: '#64748b'
          }}>
            {t('resetPassword.backToLoginFull')}{' '}
            <button
              onClick={() => navigate('/login')}
              style={{
                background: 'none',
                border: 'none',
                color: '#2B7FD7',
                fontWeight: '600',
                cursor: 'pointer',
                textDecoration: 'none',
                padding: '0'
              }}
              onMouseEnter={(e) => {
                e.target.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.target.style.textDecoration = 'none';
              }}
            >
              {t('resetPassword.backToLogin')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
